// src/AudioSpectrum/SpectrumVisualizer.js

import React, { useRef, useEffect, useCallback } from 'react';
import './SpectrumVisualizer.css';

// 각 효과 모듈 import
import { drawBars } from './effects/bars.js';
import { drawHorizontalBars } from './effects/horizontalBars.js';
import { drawMirrorBars } from './effects/mirrorBars.js';
import { drawStackedBars } from './effects/stackedBars.js';
import { drawRoundedBars } from './effects/roundedBars.js';
import { drawGradientBars } from './effects/gradientBars.js';
import { drawCircular } from './effects/circular.js';
import { drawCircularWave } from './effects/circularWave.js';
import { drawSpiral } from './effects/spiral.js';
import { drawWaveform } from './effects/waveform.js';
import { drawOscilloscope } from './effects/oscilloscope.js';
import { drawSpectrogram } from './effects/spectrogram.js';
import { drawWaterfall } from './effects/waterfall.js';
import { drawParticles } from './effects/particles.js';
import { drawGalaxy } from './effects/galaxy.js';
import { drawFireworks } from './effects/fireworks.js';
import { drawSparklers } from './effects/sparklers.js';
import { drawBurst } from './effects/burst.js';
import { drawFountain } from './effects/fountain.js';
import { drawFlowerFountain } from './effects/flowerFountain.js';
import { drawLeafFountain } from './effects/leafFountain.js';
import { drawButterflyFountain } from './effects/butterflyFountain.js';
import { drawNoteFountain } from './effects/noteFountain.js';
import { drawWavyStaff } from './effects/wavyStaff.js';
import { drawAquarium } from './effects/aquarium.js';
import { drawDancingFlowers } from './effects/dancingFlowers.js';
import { drawDancingButterfly } from './effects/dancingButterfly.js';
import { drawVibration } from './effects/vibration.js';

const SpectrumVisualizer = ({ 
  spectrumData, 
  visualizationType, 
  isPlaying, 
  fftSize, 
  language 
}) => {
  const canvasRef = useRef(null);
  const animationRef = useRef();
  
  // 각 효과별 데이터 저장 ref들
  const spectrogramData = useRef([]);
  const particlesRef = useRef([]);
  const historyRef = useRef([]);
  const fireworksRef = useRef([]);
  const sparklersRef = useRef([]);
  const burstRef = useRef([]);
  const fountainRef = useRef([]);
  const fishRef = useRef([]);
  const bubblesRef = useRef([]);
  const flowersRef = useRef([]);
  
  const draw = useCallback(() => {
    if (!canvasRef.current || !spectrumData) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { frequency, timeDomain } = spectrumData;
    
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    
    const width = rect.width;
    const height = rect.height;
    
    switch (visualizationType) {
      case 'bars':
        drawBars(ctx, frequency, width, height);
        break;
      case 'horizontalBars':
        drawHorizontalBars(ctx, frequency, width, height);
        break;
      case 'mirrorBars':
        drawMirrorBars(ctx, frequency, width, height);
        break;
      case 'stackedBars':
        drawStackedBars(ctx, frequency, width, height);
        break;
      case 'roundedBars':
        drawRoundedBars(ctx, frequency, width, height);
        break;
      case 'gradientBars':
        drawGradientBars(ctx, frequency, width, height);
        break;
      case 'circular':
        drawCircular(ctx, frequency, width, height);
        break;
      case 'circularWave':
        drawCircularWave(ctx, frequency, width, height);
        break;
      case 'spiral':
        drawSpiral(ctx, frequency, width, height);
        break;
      case 'waveform':
        drawWaveform(ctx, timeDomain, width, height);
        break;
      case 'oscilloscope':
        drawOscilloscope(ctx, timeDomain, width, height);
        break;
      case 'spectrogram':
        drawSpectrogram(ctx, frequency, width, height, spectrogramData);
        break;
      case 'waterfall':
        drawWaterfall(ctx, frequency, width, height, historyRef);
        break;
      case 'particles':
        drawParticles(ctx, frequency, width, height, particlesRef);
        break;
      case 'galaxy':
        drawGalaxy(ctx, frequency, width, height);
        break;
      case 'fireworks':
        drawFireworks(ctx, frequency, width, height, fireworksRef);
        break;
      case 'sparklers':
        drawSparklers(ctx, frequency, width, height, sparklersRef);
        break;
      case 'burst':
        drawBurst(ctx, frequency, width, height, burstRef);
        break;
      case 'fountain':
        drawFountain(ctx, frequency, width, height, fountainRef);
        break;
      case 'flowerFountain':
        drawFlowerFountain(ctx, frequency, width, height, fountainRef);
        break;
      case 'leafFountain':
        drawLeafFountain(ctx, frequency, width, height, fountainRef);
        break;
      case 'butterflyFountain':
        drawButterflyFountain(ctx, frequency, width, height, fountainRef);
        break;
      case 'noteFountain':
        drawNoteFountain(ctx, frequency, width, height, fountainRef);
        break;  
      case 'wavyStaff':
        drawWavyStaff(ctx, frequency, width, height);
        break;
      case 'aquarium':
        drawAquarium(ctx, frequency, width, height, fishRef, bubblesRef);
        break;  
      case 'dancingFlowers':
        drawDancingFlowers(ctx, frequency, width, height, flowersRef);
        break;  
      case 'dancingButterfly':
        drawDancingButterfly(ctx, frequency, width, height, flowersRef);
        break;
      case 'vibration':
        drawVibration(ctx, frequency, width, height);
        break;    
      default:
        drawBars(ctx, frequency, width, height);
    }
    
    if (isPlaying) {
      animationRef.current = requestAnimationFrame(draw);
    }
  }, [spectrumData, visualizationType, isPlaying]);

  useEffect(() => {
    if (isPlaying && spectrumData) {
      draw();
    }
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, spectrumData, draw]);

  // 시각화 타입 변경 시 데이터 초기화
  useEffect(() => {
    spectrogramData.current = [];
    historyRef.current = [];
    particlesRef.current = [];
    fireworksRef.current = [];
    sparklersRef.current = [];
    burstRef.current = [];
    fountainRef.current = [];
    fishRef.current = [];   
    bubblesRef.current = [];   
    flowersRef.current = { flowers: [], heatWaves: [], petalParticles: [] };
  }, [visualizationType]);
  
  return (
    <div className="spectrum-visualizer">
      <div className="visualizer-header">
        <h3>
          {language === 'KR' ? '스펙트럼 시각화' : 'Spectrum Visualization'} 
          ({visualizationType})
        </h3>
        <div className="fft-info">
          FFT: {fftSize}
        </div>
      </div>
      <canvas 
        ref={canvasRef}
        className="as-spectrum-canvas"
      />
    </div>
  );
};

export default SpectrumVisualizer;